import { lazy, Suspense, type ReactElement, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';

import type { RootState } from '@/app/store';
import { FullscreenLoading } from '@/components/Elements/Loading/FullscreenLoading';
import {
  REACT_APP_AUTH_INFO,
  REACT_APP_SERVICE_ID,
  REACT_APP_CUCON_API_DOMAIN,
} from '@/config';
import {
  authenticationMethodType,
  CUCON_API_PATH,
  helpLinkPath,
} from '@/constants';
import { AdminSignIn } from '@/features/authenticator/components/AdminSignIn';
import {
  fetchAuthorizationInfo,
  fetchAuthorizationTenantServiceInfo,
} from '@/features/authorizationInfo/api/fetchAuthorizationInfo';
import { amplifyMemoryStorage, setAmplifyConfig } from '@/lib/amplify';
// NOTE: jsonのプロファイルを利用する。routes/index.tsxでstore設定前にimportされているため
import { serviceProfile as jsonServiceProfile } from '@/profile/serviceProfile';
import { getLocationPath } from '@/utils/getLocationPath';
import { clearToken, setToken, CUCON_API_KEY_NAME } from '@/utils/storage';
import { useMapParams } from '@/utils/useMapParams';

const helpElement = jsonServiceProfile?.helpElement;

const Links = lazy(() =>
  import('@/features/links/components/Links').then((c) => ({
    default: c.Links,
  }))
);
const SpotList = lazy(() =>
  import('@/features/spot-list/components/SpotList').then((c) => ({
    default: c.SpotList,
  }))
);
const Help = lazy(() =>
  import('@/features/help/components/Help').then((c) => ({
    default: c.Help,
  }))
);
const Favorites = lazy(() =>
  import('@/features/favorites/components/Favorites').then((c) => ({
    default: c.Favorites,
  }))
);
const FavoritePointCreate = lazy(() =>
  import('@/features/favorites/components/FavoritePointCreate').then((c) => ({
    default: c.FavoritePointCreate,
  }))
);
const HelpDetail = lazy(() =>
  import('@/features/help/components/HelpDetail').then((c) => ({
    default: c.Help,
  }))
);
const SpotDetail = lazy(() =>
  import('@/features/spot-detail/components/SpotDetail').then((c) => ({
    default: c.SpotDetail,
  }))
);
const PopupLayerDetail = lazy(() =>
  import('@/features/spot-detail/customComponents/PopupLayerDetail').then(
    (c) => ({
      default: c.PopupLayerDetail,
    })
  )
);
const LayerDetail = lazy(() =>
  import('@/features/spot-detail/customComponents/LayerDetail').then((c) => ({
    default: c.LayerDetail,
  }))
);
const TermsOfUse = lazy(() =>
  import('@/features/help/components/TermsOfUse').then((c) => ({
    default: c.TermsOfUse,
  }))
);
const UsageGuide = lazy(() =>
  import(`@/features/help/customComponents/${helpElement}/UsageGuide`).then(
    (c) => ({
      default: c.UsageGuide,
    })
  )
);
const DataSource = lazy(() =>
  import(`@/features/help/customComponents/${helpElement}/DataSource`).then(
    (c) => ({
      default: c.DataSource,
    })
  )
);
const ButtonGuide = lazy(() =>
  import(`@/features/help/customComponents/${helpElement}/ButtonGuide`).then(
    (c) => ({
      default: c.ButtonGuide,
    })
  )
);
const BusStopInfo = lazy(() =>
  import('@/features/real-time-info-bus-stop/components/BusStopInfo').then(
    (c) => ({
      default: c.BusStopInfo,
    })
  )
);
const PostMenu = lazy(() =>
  import('@/features/post/components/PostMenu').then((c) => ({
    default: c.PostMenu,
  }))
);
const Post = lazy(() =>
  import('@/features/post/components/Post').then((c) => ({
    default: c.Post,
  }))
);
const PostList = lazy(() =>
  import('@/features/post/components/PostList').then((c) => ({
    default: c.PostList,
  }))
);

const authorizationInfoApiUrl =
  jsonServiceProfile?.cuconConfig?.authorizationInfoUrl ??
  REACT_APP_CUCON_API_DOMAIN + CUCON_API_PATH.authorizationInfo;
const authorizationTenantServiceInfoApiUrl =
  jsonServiceProfile?.cuconConfig?.authorizationTenantServiceInfoUrl ??
  REACT_APP_CUCON_API_DOMAIN + CUCON_API_PATH.authorizationTenantServiceInfo;
const authInfo =
  jsonServiceProfile?.cuconConfig?.authInfo ?? REACT_APP_AUTH_INFO;
const tenantAlias = jsonServiceProfile?.cuconConfig?.tenantAlias;
amplifyMemoryStorage.setTenantAlias(tenantAlias);
const variables = {
  tenantAlias,
  serviceId: jsonServiceProfile?.cuconConfig?.serviceId ?? REACT_APP_SERVICE_ID,
};
const authenticationMethod: string =
  jsonServiceProfile?.cuconConfig?.authenticationMethod ??
  authenticationMethodType.API_KEY;

if (
  authenticationMethod === authenticationMethodType.TOKEN ||
  authenticationMethod === authenticationMethodType.ADMIN_TOKEN
) {
  try {
    const res = await fetchAuthorizationInfo(
      tenantAlias,
      authorizationInfoApiUrl,
      authInfo
    );
    if (res.data?.authorizationInfo[0] !== undefined) {
      setAmplifyConfig(res.data?.authorizationInfo[0]);
    }
  } catch (error) {
    console.error('getAuthorizationInfo: ', error);
  }
}

if (
  jsonServiceProfile?.cuconConfig?.apiKey == null &&
  authenticationMethod !== authenticationMethodType.TOKEN
) {
  // プロファイルにapiKeyの記述がない場合のみapiKeyを取得する
  try {
    const res = await fetchAuthorizationTenantServiceInfo(
      variables,
      authorizationTenantServiceInfoApiUrl,
      authInfo
    );
    const apiKey = res.data?.authorizationTenantServiceInfo[0].apiKey ?? '';

    setToken(CUCON_API_KEY_NAME, apiKey);
    if (res.data?.authorizationTenantServiceInfo[0] !== undefined) {
      setAmplifyConfig(res.data?.authorizationTenantServiceInfo[0]);
    }
  } catch (error) {
    console.error('getAuthorizationTenantServiceInfo: ', error);
    clearToken(CUCON_API_KEY_NAME);
  }
}

const Loading = () => {
  const { navigationHeight } = useSelector((state: RootState) => state.drawer);

  return (
    <div
      data-testid="loading-overlay"
      className={
        ' visible fixed top-0 z-[101] w-full bg-black/60 opacity-100 duration-300 ease-in'
      }
      style={{ height: `calc(100% - ${navigationHeight}px)` }}
    >
      <FullscreenLoading />
    </div>
  );
};

const FavoritesRoute = ({
  children,
}: {
  children: ReactNode;
}): ReactElement => {
  const { serviceProfile } = useSelector(
    (state: RootState) => state.serviceProfile
  );
  const { currentLocation, zoom } = useSelector(
    (state: RootState) => state.map
  );
  const { mapType } = useMapParams();
  const location = useLocation();

  if (serviceProfile.menu?.enableFavorites !== true) {
    return (
      <Navigate
        replace
        to={`${getLocationPath(
          currentLocation.latitude,
          currentLocation.longitude,
          zoom,
          '/',
          mapType
        )}${location.search}`}
      />
    );
  }

  return <>{children}</>;
};

export const publicRoutes = {
  children: [
    {
      path: 'home',
      element: <></>,
    },
    {
      path: 'spots',
      element: (
        <Suspense fallback={<Loading />}>
          <SpotList />
        </Suspense>
      ),
    },
    {
      path: 'spots/spot-detail/:spotTypeId/:spotId',
      element: (
        <Suspense fallback={<Loading />}>
          <SpotDetail />
        </Suspense>
      ),
    },
    {
      path: 'search/:spotType/:id',
      element: (
        <Suspense fallback={<Loading />}>
          <SpotDetail />
        </Suspense>
      ),
    },
    {
      path: 'popup/layer/:layerId',
      element: (
        <Suspense fallback={<Loading />}>
          <PopupLayerDetail />
        </Suspense>
      ),
    },
    {
      path: 'layers/layer-detail/:layerId',
      element: (
        <Suspense fallback={<Loading />}>
          <LayerDetail />
        </Suspense>
      ),
    },
    {
      path: 'links',
      element: (
        <Suspense fallback={<Loading />}>
          <Links />
        </Suspense>
      ),
    },
    {
      path: 'admin-signin',
      element: (
        <Suspense fallback={<Loading />}>
          <AdminSignIn />
        </Suspense>
      ),
    },
    {
      path: 'help',
      element: (
        <Suspense fallback={<Loading />}>
          <Help />
        </Suspense>
      ),
    },
    {
      path: 'favorites',
      element: (
        <Suspense fallback={<Loading />}>
          <FavoritesRoute>
            <Favorites />
          </FavoritesRoute>
        </Suspense>
      ),
    },
    {
      path: 'favorites/point/create',
      element: (
        <Suspense fallback={<Loading />}>
          <FavoritesRoute>
            <FavoritePointCreate />
          </FavoritesRoute>
        </Suspense>
      ),
    },
    {
      path: 'help/:id',
      element: (
        <Suspense fallback={<Loading />}>
          <HelpDetail />
        </Suspense>
      ),
    },
    {
      path: `help/${helpLinkPath.USAGE_GUIDE}`,
      element: (
        <Suspense fallback={<Loading />}>
          <UsageGuide />
        </Suspense>
      ),
    },
    {
      path: `help/${helpLinkPath.TERMS_OF_USE}`,
      element: (
        <Suspense fallback={<Loading />}>
          <TermsOfUse />
        </Suspense>
      ),
    },
    {
      path: `help/${helpLinkPath.DATA_SOURCE}`,
      element: (
        <Suspense fallback={<Loading />}>
          <DataSource />
        </Suspense>
      ),
    },
    {
      path: `help/${helpLinkPath.BUTTON_GUIDE}`,
      element: (
        <Suspense fallback={<Loading />}>
          <ButtonGuide />
        </Suspense>
      ),
    },
    {
      path: 'real-time-info/bus-stop/:busStopId',
      element: <BusStopInfo />,
    },
    {
      path: 'post',
      element: (
        <Suspense fallback={<Loading />}>
          <PostMenu />
        </Suspense>
      ),
    },
    {
      path: 'post/:action',
      element: (
        <Suspense fallback={<Loading />}>
          <Post />
        </Suspense>
      ),
    },
    {
      path: 'post/:action/:id',
      element: (
        <Suspense fallback={<Loading />}>
          <Post />
        </Suspense>
      ),
    },
    {
      path: 'post/list',
      element: (
        <Suspense fallback={<Loading />}>
          <PostList />
        </Suspense>
      ),
    },
  ],
};
